import React, { useCallback, useState } from "react";
import { Flex, Button, Text } from "@radix-ui/themes";

interface QuestionsContextProps {
  options: string[];
  onQuestionClick: (question: string) => Promise<void>;
}

interface QuestionItemProps {
  option: string;
  onQuestionClick: (question: string) => Promise<void>;
}

function QuestionItem({ option, onQuestionClick }: QuestionItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = useCallback(() => {
    onQuestionClick(option);
  }, [option, onQuestionClick]);

  return (
    <div
      style={{
        textAlign: "left",
      }}
    >
      <span
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          cursor: "pointer",
          display: "inline",
          borderRadius: "4px",
          border: `1px solid ${isHovered ? "#B8D9F5" : "#EDF6FF"}`,
          background: isHovered ? "#D4E8F8" : "#EDF6FF",
          color: "rgba(0, 5, 9, 0.89)",
          fontSize: "13px",
          fontWeight: 400,
          lineHeight: "20px",
          whiteSpace: "normal",
          wordBreak: "break-word",
          padding: "4px 8px",
          boxSizing: "border-box",
          boxDecorationBreak: "clone",
          WebkitBoxDecorationBreak: "clone",
          transition: "background 0.2s ease, border-color 0.2s ease",
        }}
        aria-label={`Ask question: ${option}`}
      >
        {option}
      </span>
    </div>
  );
}

export function QuestionsContext({
  options,
  onQuestionClick,
}: QuestionsContextProps) {
  const handleQuestionClick = useCallback(
    async (question: string) => {
      await onQuestionClick(question);
    },
    [onQuestionClick]
  );

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <Flex px={"3"} py={"2"} direction={"column"} gap={"2"}>
      <Text size={"1"} color={"gray"}>
        For you
      </Text>
      <Flex
        gap={"2"}
        direction={"column"}
        style={{
          width: "100%",
          alignItems: "flex-start",
        }}
      >
        {options.map((option, index) => (
          <QuestionItem
            key={`question-${index}`}
            option={option}
            onQuestionClick={handleQuestionClick}
          />
        ))}
      </Flex>
    </Flex>
  );
}
